import { useState, createContext, useContext } from 'react';
import ToastContext from 'src/store/toast-context';
import { useModal } from 'src/services/modal';
const AssetTableContext = createContext();
import { useAPI } from 'src/services/api';

export default AssetTableContext;

const assetDefaultValues = {
  assetId: '',
  itemDescr: '',
  propCode: '',
  purchaseCost: '',
  quantity: '',
  yearAcq: '',
  lesseeAddr: '',
  lesseeCity: '',
  lesseeName: '',
  isLeased: false,
};

/**
 * Custom hook that manages the state of assets, propDescriptions, propCode, updating, and initialValues.
 *
 * @return {object} object containing the following properties:
 * - assets: An array of asset objects.
 * - setAssets: A function that sets the value of assets.
 * - updating: A boolean value indicating whether the component is currently updating.
 * - setUpdating: A function that sets the value of updating.
 * - setInitialValues: A function that sets the value of initialValues.
 * - initialValues: An object containing default values for asset form fields.
 * - updateAssets: A function that updates the value of assets.
 * - propCode: An array of code objects.
 * - setPropCode: A function that sets the value of propCode.
 * - propDescriptions: An array of prop description objects.
 * - setPropDescriptions: A function that sets the value of propDescriptions.
 * */
const useAssetInfo = () => {
  const [assets, setAssets] = useState([]);
  const [propDescriptions, setPropDescriptions] = useState([]);
  const [propCode, setPropCode] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [initialValues, setInitialValues] = useState(assetDefaultValues);

  const updateAssets = (assets) => {
    return setAssets(assets);
  };

  return {
    assets,
    setAssets,
    updating,
    setUpdating,
    setInitialValues,
    initialValues,
    updateAssets,
    propCode,
    setPropCode,
    propDescriptions,
    setPropDescriptions,
  };
};

/**
 * Hook to manage the state of the asset modal.
 * @param {Object} param0 - Object containing the setUpdating and setInitialValues functions
 * @param {function} param0.setUpdating - Function to set the updating state
 * @param {function} param0.setInitialValues - Function to set the initial values of the form
 * @returns {Object} Object containing the assetModalController, assetDefaultValues, and resetAssetModal functions
 */
const useAssetModal = ({ setUpdating, setInitialValues }) => {
  const assetModalController = useModal();

  const resetAssetModal = () => {
    setUpdating(null);
    setInitialValues(assetDefaultValues);
    assetModalController.closeModal();
  };

  return {
    assetModalController,
    assetDefaultValues,
    resetAssetModal,
  };
};

/**
 * Hook to manage the state of the upload modal.
 * @param {Object} param0 - Object containing the updateAssets function
 * @param {function} param0.updateAssets - Function to update the list of assets
 * @returns {Object} Object containing various functions and state values for the upload modal
 */
const useUploadModal = ({ updateAssets }) => {
  const api = useAPI();
  const uploadModalController = useModal();
  const [currentFile, setCurrentFile] = useState(null);
  const [uploadLoaded, setUploadLoaded] = useState(true);
  const [spreadsheetUploadError, setSpreadsheetUploadError] = useState('');
  const { success } = useContext(ToastContext);

  const uploadSpreadsheet = async () => {
    setUploadLoaded(false);
    try {
      const data = await postSpreadsheet();
      handleSuccessfulUpload(data);
    } catch (err) {
      handleFailedUpload(err);
    }
  };

  const postSpreadsheet = async () => {
    const formData = new FormData();
    formData.append('file', currentFile);
    const { data } = await api.post(`/assets/spreadsheet`, formData);
    return data;
  };

  const handleSuccessfulUpload = (data) => {
    setUploadLoaded(true);
    setSpreadsheetUploadError('');
    updateAssets(data);
    uploadModalController.closeModal();
    success('Success');
    setCurrentFile(null);
  };

  const handleFailedUpload = (err) => {
    setUploadLoaded(true);
    setSpreadsheetUploadError(err.response ? err.response.data : err.message);
    console.error(err);
  };

  const onDrop = (files) => {
    if (files[0]) {
      setCurrentFile(files[0]);
    }
  };

  const onRemove = () => {
    setCurrentFile(null);
    setSpreadsheetUploadError('');
  };

  return {
    onRemove,
    onDrop,
    uploadSpreadsheet,
    uploadLoaded,
    spreadsheetUploadError,
    uploadModalController,
    setCurrentFile,
    currentFile,
  };
};

/**
 * Hook to manage the state of the help modal.
 * @returns {Object} Object containing the helpModalController function
 */
const useHelpModal = () => {
  const helpModalController = useModal();

  return { helpModalController };
};

/**
 * Provider for the AssetTableContext.
 * @param {Object} props - Properties of the component
 * @param {Object} props.children - Child elements of the component
 * @returns {JSX.Element} The AssetTableProvider component
 */
export const AssetTableProvider = (props) => {
  const { setUpdating, setInitialValues, setAssets, ...rest } = useAssetInfo();

  const assetModal = useAssetModal({ setUpdating, setInitialValues });

  const uploadModal = useUploadModal({
    updateAssets: setAssets,
  });

  const helpModal = useHelpModal();

  return (
    <AssetTableContext.Provider
      value={{
        uploadModal,
        assetModal,
        helpModal,
        assetInfo: { setUpdating, setInitialValues, setAssets, ...rest },
      }}
    >
      {props.children}
    </AssetTableContext.Provider>
  );
};
